import React from 'react';
import { designTokens } from '../../theme/designTokens';

export type CardVariant = 'default' | 'compact' | 'large' | 'hover' | 'gray' | 'grayCompact';

export interface CardProps {
  /**
   * Card style variant
   * @default 'default'
   */
  variant?: CardVariant;

  /**
   * Card title (optional header)
   */
  title?: React.ReactNode;

  /**
   * Card subtitle/description
   */
  subtitle?: React.ReactNode;

  /**
   * Action buttons/elements for the header
   */
  actions?: React.ReactNode;

  /**
   * Card content
   */
  children: React.ReactNode;

  /**
   * Click handler (makes card interactive)
   */
  onClick?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom padding (overrides variant padding)
   */
  padding?: string;
}

/**
 * Card Component
 *
 * Standardized card container with consistent styling.
 * Supports different variants and optional header with title/actions.
 *
 * @example
 * ```tsx
 * <Card title="Project Details">
 *   <p>Content here</p>
 * </Card>
 *
 * <Card variant="hover" onClick={() => navigate('/project/1')}>
 *   <h3>Clickable Card</h3>
 * </Card>
 *
 * <Card
 *   title="Active Projects"
 *   actions={<Button size="sm">View All</Button>}
 * >
 *   <ProjectList />
 * </Card>
 * ```
 */
export const Card: React.FC<CardProps> = ({
  variant = 'default',
  title,
  subtitle,
  actions,
  children,
  onClick,
  className = '',
  padding,
}) => {
  const variantClass = designTokens.cards[variant];
  const isInteractive = !!onClick;

  // If custom padding provided, use base card + custom padding
  const cardClassName = padding
    ? `${designTokens.cards.base} ${padding}`
    : variantClass;

  const finalClassName = `
    ${cardClassName}
    ${isInteractive ? 'cursor-pointer' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const cardContent = (
    <>
      {(title || subtitle || actions) && (
        <div className={`flex items-start justify-between ${children ? 'mb-4' : ''}`}>
          <div className="flex-1">
            {title && (
              <h3 className={designTokens.typography.h4.className}>
                {title}
              </h3>
            )}
            {subtitle && (
              <p className={`${designTokens.typography.small.className} ${title ? 'mt-1' : ''}`}>
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex-shrink-0 ml-4">
              {actions}
            </div>
          )}
        </div>
      )}
      {children}
    </>
  );

  if (isInteractive) {
    return (
      <div
        className={finalClassName}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick();
          }
        }}
      >
        {cardContent}
      </div>
    );
  }

  return <div className={finalClassName}>{cardContent}</div>;
};

export default Card;
